/**
 * V2 admin · Apartment detail (COL-225).
 *
 * Owner-scoped view of one apartment within a property. Mirrors the
 * Property detail layout: tabbed content (Rooms · Owner · Contract · Bank ·
 * Revenue) plus a right-hand summary sidebar. Read-only in phase 1 — the
 * Edit / Add room actions are present but stubbed.
 *
 * Route: /admin/properties/:id/apartments/:aptId
 */
import { useEffect, useMemo, useState } from "react";
import { useScrollToTopOnChange } from "@/hooks/useScrollToTopOnChange";
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  Link2,
  Pencil,
  Plus,
} from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NotesPanel } from "@/components/admin/v2/notes/NotesPanel";
import { useOpenNoteCount } from "@/hooks/v2/useEntityNotes";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { RoomsTab } from "@/components/admin/v2/property-tabs/RoomsTab";
import { PropertyGlobalSearch } from "@/components/admin/v2/property-tabs/PropertyGlobalSearch";
import { ApartmentEditSheet } from "@/components/admin/v2/ApartmentEditSheet";
import {
  PAYMENT_CONFIG_EDIT_PARAM,
  PAYMENT_CONFIG_EDIT_SECTION,
} from "@/lib/payment-configuration-link";
import { RoomCreateSheet } from "@/components/admin/v2/RoomCreateSheet";
import { AssignRoomsDialog } from "@/components/admin/v2/AssignRoomsDialog";
import { InheritanceBadge } from "@/components/admin/v2/InheritanceBadge";
import { OwnerDetailModal } from "@/components/admin/v2/OwnerDetailModal";
import { useLessor } from "@/hooks/v2/useLessors";
import { useOwnerOnboardingDocs } from "@/hooks/v2/useOwnerOnboardingDocs";
import { useLessorIdByOwnerUser } from "@/hooks/v2/owner-user-lessor";
import { effectiveApartmentOwner } from "@/lib/owner-metrics";
import {
  computeApartmentEconomics,
  resolveExpectedMonthRent,
  type ApartmentExpectedRent,
} from "@/lib/apartment-economics";
import {
  deriveApartmentOccupancy,
  usePropertyOccupiedRoomIds,
  resolveEffectiveBank,
  resolveEffectiveRep,
  resolveEffectiveRevenue,
  resolvePlatformAccount,
  useApartment,
  useApartmentBankOverride,
  useApartmentRooms,
  usePlatformBankAccounts,
  usePlatformLessor,
  useProperty,
} from "@/hooks/v2/useProperties";
import {
  usePropertyActiveOccupants,
  type ActiveOccupant,
} from "@/hooks/v2/useReassignBookings";
import {
  currentMonthKey,
  monthKeyLabel,
  recentMonthKeys,
  useApartmentMonthRent,
  EMPTY_APARTMENT_MONTH_RENT,
  type ApartmentMonthRent,
  type ApartmentMonthRentRow,
} from "@/hooks/v2/useApartmentMonthRent";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { ApartmentDocumentsTab } from "@/components/admin/v2/apartment/ApartmentDocumentsTab";
import { ApartmentChecklistCard } from "@/components/admin/v2/apartment/ApartmentChecklistCard";
import { formatDate, formatEUR, initialsFromName } from "@/lib/v2-format";
import type { Apartment, Room } from "@/types/property";
import { collectionModeLabels } from "@/lib/collection-mode";
import type { Lessor } from "@/types/lessor";
import type {
  EffectiveBank,
  EffectiveRep,
  EffectiveRevenue,
  PlatformBankAccount,
} from "@/types/property";
import { useLanguage } from "@/contexts/LanguageContext";
import { StoredFileLink } from "@/components/shared/StoredFileLink";

// Staff-facing: the logged-in user is not the owner, so no name.
const COLLECTION_MODE_LABEL: Record<string, string> = collectionModeLabels();

// Tab chrome: plain labels on a hairline rule, the active one marked by a
// petrol underline rather than a filled pill — the brand's flat tab styling.
const TAB_LIST_CLASS =
  "h-auto w-full justify-start gap-6 rounded-none border-b bg-transparent p-0";
const TAB_TRIGGER_CLASS =
  "rounded-none border-b-2 border-transparent bg-transparent px-0 pb-2.5 pt-1 font-medium text-muted-foreground shadow-none transition-colors hover:text-foreground data-[state=active]:border-petrol data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-petrol data-[state=active]:shadow-none";

// Revenue-model labels, translated at render time (no hook access at module
// scope) by every caller that already has `t` in hand.
function revenueModelLabel(
  model: string,
  t: (key: string, fallback?: string) => string
): string {
  switch (model) {
    case "full_rent":
      return t("admin.apartments.revenueModelFullRent", "Rent arbitrage");
    case "pct_of_rent":
      return t("admin.apartments.revenueModelPctOfRent", "Percentage of rent");
    case "flat_fee_per_room":
      return t(
        "admin.apartments.revenueModelFlatFeePerRoom",
        "Flat fee per room"
      );
    default:
      return model;
  }
}

type FlowAccount = {
  party: "Owner" | "coliviq";
  holder: string | null;
  bank_name: string | null;
  iban: string | null;
  bic: string | null;
};

/**
 * Map the two bank accounts onto their money-flow roles. The "rent receipt"
 * account is the one tenant rent flows into — this is the only account tenants
 * see in their dashboard and that Stripe is wired to.
 */
function resolveMoneyFlow(
  companyCollects: boolean,
  effectiveBank: EffectiveBank,
  coliviqAccount: PlatformBankAccount | null,
  t: (key: string, fallback?: string) => string
): {
  rentReceipt: FlowAccount;
  secondary: FlowAccount;
  secondaryLabel: string;
} {
  const ownerAcct: FlowAccount = {
    party: "Owner",
    holder: effectiveBank.holder,
    bank_name: effectiveBank.bank_name,
    iban: effectiveBank.iban,
    bic: effectiveBank.bic,
  };
  const coliviqAcct: FlowAccount = {
    party: "coliviq",
    holder: coliviqAccount?.holder ?? null,
    bank_name: coliviqAccount?.bank_name ?? null,
    iban: coliviqAccount?.iban ?? null,
    bic: coliviqAccount?.bic ?? null,
  };
  return companyCollects
    ? {
        rentReceipt: coliviqAcct,
        secondary: ownerAcct,
        secondaryLabel: t(
          "admin.apartments.netRentPaidToOwner",
          "Net rent paid to owner"
        ),
      }
    : {
        rentReceipt: ownerAcct,
        secondary: coliviqAcct,
        secondaryLabel: t(
          "admin.apartments.feePaidToColiviq",
          "Fee paid to coliviq"
        ),
      };
}

function maskIban(iban: string | null): string {
  if (!iban) return "—";
  return `${iban.slice(0, 4)}…${iban.slice(-4)}`;
}

export default function V2ApartmentDetail() {
  const { t } = useLanguage();
  const { id, aptId } = useParams<{ id: string; aptId: string }>();
  const propertyId = id ? Number(id) : null;
  const apartmentId = aptId ? Number(aptId) : null;
  // `?room=<id>` deep-link (from the global search) — the Rooms tab scrolls to
  // and flashes that room on arrival.
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightRoomId = searchParams.get("room")
    ? Number(searchParams.get("room"))
    : null;
  // `?edit=revenue` deep link (COL-287): a management-invoice row's "Payment
  // configuration" action lands here with the edit sheet open on its
  // Revenue & fees section. Dropped when the sheet closes — see PropertyDetail.
  const editParam = searchParams.get(PAYMENT_CONFIG_EDIT_PARAM);
  const focusSection =
    editParam === PAYMENT_CONFIG_EDIT_SECTION ? "revenue" : null;

  const { data: property } = useProperty(propertyId);
  const {
    data: apartment,
    isLoading: aLoading,
    error: aError,
  } = useApartment(apartmentId);
  const { data: rooms = [], isLoading: rLoading } =
    useApartmentRooms(apartmentId);
  // At page level, not inside the tab: Radix unmounts inactive tabs, so a count
  // living in the panel would only appear once you were already looking at it.
  const openNoteCount = useOpenNoteCount(
    apartmentId === null ? null : { level: "apartment", apartmentId }
  );
  // EFFECTIVE owner, in the order the Owners surfaces resolve it: the
  // apartment's own lessor → the lessor behind its owner ACCOUNT
  // (apartments.owner_user_id, which the apartment editor writes when you pick
  // an owner profile) → the lessor holding its property. Reading only
  // apartment.lessor_id showed "Unassigned" for both inherited and
  // owner-account apartments, contradicting the Owners page and the invoices.
  const { data: lessorByOwnerUser = {} } = useLessorIdByOwnerUser();
  const accountLessorId = apartment?.owner_user_id
    ? (lessorByOwnerUser[apartment.owner_user_id] ?? null)
    : null;
  const effectiveLessorId = effectiveApartmentOwner(
    apartment?.lessor_id,
    property?.lessor_id,
    accountLessorId
  );
  const ownerInherited =
    !apartment?.lessor_id && !accountLessorId && !!property?.lessor_id;
  // The owner this APARTMENT holds, as opposed to the one it merely inherits:
  // the first two steps above, without the property fallback. The inheritance
  // resolvers need the distinction — an inherited apartment must keep falling
  // through to the property's own overrides, and `owner` alone can't say which
  // of the three steps produced it.
  const apartmentOwnLessorId = apartment?.lessor_id ?? accountLessorId ?? null;
  // An owner ACCOUNT is named but resolves to no lessor record — the apartment
  // looks owned here yet is invisible to Owners and to billing, which key on
  // lessor_id. Worth saying out loud rather than rendering a bare "Unassigned".
  const ownerAccountUnlinked =
    !!apartment?.owner_user_id && !apartment?.lessor_id && !accountLessorId;
  const { data: owner } = useLessor(effectiveLessorId);
  const { data: apartmentBank } = useApartmentBankOverride(apartmentId);
  const { data: platformAccounts = [] } = usePlatformBankAccounts();
  const { data: coliviqLessor } = usePlatformLessor();
  const { data: occupants = new Map() } =
    usePropertyActiveOccupants(propertyId);
  // Occupancy follows the Room detail rule, which also counts tenants in their
  // notice period (the occupants map above holds active bookings only).
  const { data: occupiedRoomIds = new Set<number>() } =
    usePropertyOccupiedRoomIds(propertyId);
  const [editOpen, setEditOpen] = useState(false);
  useEffect(() => {
    if (focusSection) setEditOpen(true);
  }, [focusSection]);
  const handleEditOpenChange = (next: boolean) => {
    setEditOpen(next);
    if (!next && editParam != null) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete(PAYMENT_CONFIG_EDIT_PARAM);
          return params;
        },
        { replace: true }
      );
    }
  };
  const [assignOpen, setAssignOpen] = useState(false);
  const [createRoomOpen, setCreateRoomOpen] = useState(false);
  const [tab, setTab] = useState("rooms");
  useScrollToTopOnChange(tab);
  // Which calendar month the Revenue tab and the sidebar report "live" rent
  // for. One piece of state for both, so the two can never quote different
  // months at each other.
  const [revenueMonth, setRevenueMonth] = useState(() => currentMonthKey());
  // Owner profile popup — the admin OwnerDetailModal, opened by clicking the
  // owner's name in the sidebar / Owner / Contract tabs. Owner-portal users
  // can't reach the admin owner-detail data (RLS + it's a management surface),
  // so the name is only interactive for admins.
  const { isOwnerPortalOnly } = useAuth();
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const openOwner =
    !isOwnerPortalOnly && owner ? () => setOwnerModalOpen(true) : undefined;

  const effectiveBank = useMemo(
    () =>
      resolveEffectiveBank({
        apartmentBank: apartmentBank ?? null,
        apartmentLessor: owner ?? null,
        lessor: owner ?? null,
      }),
    [apartmentBank, owner]
  );

  // `apartmentLessor` is passed ONLY when the apartment holds that owner itself
  // (own lessor_id, or its owner ACCOUNT) — never when `owner` is just the
  // property's lessor showing through. An inherited apartment has to reach
  // `property.rep_override`, and the resolver can't tell an inherited owner
  // from an owned one. Passing `owner` unfiltered here made this tab print the
  // property's override for owner-account apartments while create-contract —
  // which folds the same owner bridge into its aptLessorId — printed the
  // owner's rep on the tenant's actual contract.
  const effectiveRep = useMemo(
    () =>
      resolveEffectiveRep({
        apartment: apartment ?? null,
        apartmentLessor: apartmentOwnLessorId ? (owner ?? null) : null,
        property: property ?? null,
        lessor: owner ?? null,
      }),
    [apartment, apartmentOwnLessorId, owner, property]
  );

  const effectiveRevenue = useMemo(
    () =>
      resolveEffectiveRevenue({
        apartment: apartment ?? null,
        property: property ?? null,
      }),
    [apartment, property]
  );

  const coliviqAccount = useMemo(
    () =>
      resolvePlatformAccount({
        apartment: apartment ?? null,
        property: property ?? null,
        accounts: platformAccounts,
        coliviqLessor: coliviqLessor ?? null,
      }),
    [apartment, property, platformAccounts, coliviqLessor]
  );

  // The account rent flows into must exist, or tenants have nothing to pay.
  const companyCollects =
    effectiveRevenue.collection_mode === "company_collects";
  const rentReceiptMissing = companyCollects
    ? !coliviqAccount?.iban
    : !effectiveBank.iban;

  const visibleRooms = rooms.filter((r) => !r.is_hidden);
  // Occupancy from active bookings (single source of truth), not stale
  // room.status / room.available.
  const occupied = visibleRooms.filter((r) =>
    occupiedRoomIds.has(Number(r.id))
  ).length;
  // Apartment occupancy is derived from active bookings (single source of
  // truth) — never the manual, drift-prone apartments.rental_status field.
  const occupancy = deriveApartmentOccupancy(occupied, visibleRooms.length);
  const occupancyLabel =
    occupancy === "occupied"
      ? t("admin.apartments.occupied", "Occupied")
      : occupancy === "partial"
        ? t("admin.apartments.partiallyOccupied", "Partially occupied")
        : t("admin.apartments.vacant", "Vacant");
  // Rent that actually arrived for the selected month — the "live" half of
  // every revenue figure below. Read once here and handed to both the Revenue
  // tab and the sidebar.
  const visibleRoomIds = useMemo(
    () => visibleRooms.map((r) => r.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleRooms.map((r) => r.id).join(",")]
  );
  const { data: monthRent, isPending: monthRentPending } =
    useApartmentMonthRent(visibleRoomIds, revenueMonth);
  const liveRent = monthRent ?? EMPTY_APARTMENT_MONTH_RENT;
  const liveRentLoading = visibleRoomIds.length > 0 && monthRentPending;
  // The "expected" half: contracted rent for the same month. Not the sum of
  // every room's list price — a vacant room is under no contract, and billing
  // one would forecast rent nobody owes and a per-room fee we never invoice.
  const expectedRent = useMemo(
    () =>
      resolveExpectedMonthRent(
        visibleRooms,
        new Set(occupants.keys()),
        liveRent.rows
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleRoomIds, occupants, liveRent.rows]
  );

  // ─── Loading ──────────────────────────────────────────────────────────
  if (aLoading || (apartmentId && !apartment && !aError)) {
    return (
      <AdminV2Layout
        title={t("common.loading", "Loading…")}
        breadcrumbs={[
          { label: t("admin.nav.admin", "Admin"), to: "/admin" },
          {
            label: t("admin.nav.properties", "Properties"),
            to: "/admin/properties",
          },
          { label: t("common.loading", "Loading…") },
        ]}
      >
        <div className="space-y-4">
          <Skeleton className="h-10 w-72" />
          <Skeleton className="h-96 w-full" />
        </div>
      </AdminV2Layout>
    );
  }

  // ─── Not found ────────────────────────────────────────────────────────
  if (aError || !apartment) {
    return (
      <AdminV2Layout
        title={t("admin.apartments.notFound", "Apartment not found")}
        breadcrumbs={[
          { label: t("admin.nav.admin", "Admin"), to: "/admin" },
          {
            label: t("admin.nav.properties", "Properties"),
            to: "/admin/properties",
          },
          { label: t("admin.apartments.notFound", "Apartment not found") },
        ]}
      >
        <Card className="p-8 space-y-3 max-w-lg">
          <h2 className="text-lg font-semibold">
            {t("admin.apartments.notFound", "Apartment not found")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(
              "admin.apartments.notFoundDesc",
              "This apartment may have been removed or you may not have access."
            )}
          </p>
          <Button asChild variant="outline" size="sm">
            <Link
              to={
                propertyId
                  ? `/admin/properties/${propertyId}`
                  : "/admin/properties"
              }
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("admin.apartments.backToProperty", "Back to property")}
            </Link>
          </Button>
        </Card>
      </AdminV2Layout>
    );
  }

  const propertyTitle =
    property?.title ?? t("admin.properties.propertyNoun", "Property");

  return (
    <AdminV2Layout
      title={`${propertyTitle} · ${apartment.label}`}
      breadcrumbs={[
        { label: t("admin.nav.admin", "Admin"), to: "/admin" },
        {
          label: t("admin.nav.properties", "Properties"),
          to: "/admin/properties",
        },
        ...(propertyId
          ? [{ label: propertyTitle, to: `/admin/properties/${propertyId}` }]
          : []),
        { label: apartment.label },
      ]}
      actions={
        <>
          <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            {t("admin.apartments.editApartment", "Edit apartment")}
          </Button>
          <Button size="sm" onClick={() => setAssignOpen(true)}>
            <Link2 className="mr-2 h-4 w-4" />
            {t("admin.apartments.assignRooms", "Assign rooms")}
          </Button>
          <Button size="sm" onClick={() => setCreateRoomOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("admin.rooms.roomColumn", "Room")}
          </Button>
        </>
      }
      sidebar={
        <ApartmentSidebar
          apartment={apartment}
          owner={owner ?? null}
          ownerInherited={ownerInherited}
          onOpenOwner={openOwner}
          rooms={visibleRooms}
          expectedRent={expectedRent}
          occupied={occupied}
          effectiveBank={effectiveBank}
          coliviqAccount={coliviqAccount}
          revenue={effectiveRevenue}
          liveRent={liveRent}
          liveRentLoading={liveRentLoading}
          revenueMonth={revenueMonth}
        />
      }
    >
      {/* Meta strip */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <Badge variant="outline">
          {apartment.floor
            ? `${t("admin.apartments.floor", "Floor")} ${apartment.floor}`
            : `${t("admin.apartments.floor", "Floor")} —`}
        </Badge>
        {apartment.size_sqm != null && (
          <Badge variant="outline">{apartment.size_sqm} m²</Badge>
        )}
        {apartment.num_rooms != null && (
          <Badge variant="outline">
            {apartment.num_rooms}{" "}
            {apartment.num_rooms === 1
              ? t("admin.rooms.roomNoun", "room")
              : t("admin.rooms.roomsNoun", "rooms")}
          </Badge>
        )}
        {visibleRooms.length > 0 && (
          <Badge variant={occupancy === "occupied" ? "default" : "secondary"}>
            {occupancyLabel}
          </Badge>
        )}
        <Badge
          variant={
            occupied >= visibleRooms.length && visibleRooms.length > 0
              ? "default"
              : "outline"
          }
        >
          {occupied}/{visibleRooms.length}{" "}
          {t("admin.properties.occupied", "Occupied")}
        </Badge>
        <div className="flex-1" />
        {apartment.lease_end && (
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            {t("admin.apartments.contractEnds", "Contract ends")}{" "}
            {formatDate(apartment.lease_end)}
          </span>
        )}
      </div>

      {rentReceiptMissing && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {companyCollects ? (
              <>
                {t(
                  "admin.apartments.noColiviqBankBanner",
                  "No coliviq bank account is configured for this apartment, so tenants have no account to pay rent into. Set the coliviq account under"
                )}{" "}
                <strong>
                  {t(
                    "admin.apartments.editApartmentBank",
                    "Edit apartment → Bank"
                  )}
                </strong>
                .
              </>
            ) : (
              <>
                {t(
                  "admin.apartments.noOwnerBankBanner",
                  "Rent is collected by the owner, but no owner bank account is set. Tenants can't pay rent until you add the owner's bank details under"
                )}{" "}
                <strong>
                  {t(
                    "admin.apartments.editApartmentBank",
                    "Edit apartment → Bank"
                  )}
                </strong>{" "}
                {t(
                  "admin.apartments.noOwnerBankBannerSuffix",
                  "— card and SEPA are disabled for owner-collected rent."
                )}
              </>
            )}
          </span>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className={TAB_LIST_CLASS}>
          <TabsTrigger value="rooms" className={TAB_TRIGGER_CLASS}>
            {t("admin.rooms.roomsNoun", "Rooms")}
            <span className="ml-1.5 text-xs opacity-60 tabular-nums">
              ({visibleRooms.length})
            </span>
          </TabsTrigger>
          <TabsTrigger value="owner" className={TAB_TRIGGER_CLASS}>
            {t("admin.apartments.ownerTab", "Owner")}
          </TabsTrigger>
          <TabsTrigger value="bank" className={TAB_TRIGGER_CLASS}>
            {t("admin.apartments.bankTab", "Bank")}
          </TabsTrigger>
          <TabsTrigger value="revenue" className={TAB_TRIGGER_CLASS}>
            {t("admin.apartments.revenueTab", "Revenue")}
          </TabsTrigger>
          <TabsTrigger value="documents" className={TAB_TRIGGER_CLASS}>
            {t("admin.apartments.documentsTab", "Documents")}
          </TabsTrigger>
          <TabsTrigger
            value="notes"
            className={`relative ${TAB_TRIGGER_CLASS}`}
          >
            {t("admin.apartments.notesTab", "Notes")}
            {openNoteCount > 0 && (
              <span
                className="absolute -right-2 -top-1.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full border-2 border-background bg-petrol px-1 text-[9px] font-bold leading-none text-white shadow-sm"
                title={`${openNoteCount} ${t("admin.apartments.openNotes", "open {{count}}").replace("{{count}}", openNoteCount === 1 ? t("admin.apartments.noteSingular", "note") : t("admin.apartments.noteNounPlural", "notes"))}`}
                aria-label={`${openNoteCount} ${t("admin.apartments.openNotes", "open {{count}}").replace("{{count}}", t("admin.apartments.noteNounPlural", "notes"))}`}
              >
                {openNoteCount > 99 ? "99+" : openNoteCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Global search — one box under the tab bar that queries the whole
            portfolio (apartments, rooms, tenants, lessors) and routes straight
            to each match. Matches in this property rank first. */}
        <PropertyGlobalSearch currentPropertyId={propertyId} />

        <TabsContent value="rooms">
          {rLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-md" />
              ))}
            </div>
          ) : propertyId ? (
            <RoomsTab
              propertyId={propertyId}
              rooms={rooms}
              highlightRoomId={highlightRoomId}
              apartmentId={apartmentId}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="owner">
          <OwnerTab
            apartment={apartment}
            owner={owner ?? null}
            ownerInherited={ownerInherited}
            ownerAccountUnlinked={ownerAccountUnlinked}
            effectiveRep={effectiveRep}
            revenue={effectiveRevenue}
            onOpenOwner={openOwner}
          />
        </TabsContent>

        <TabsContent value="bank">
          <BankTab
            effectiveBank={effectiveBank}
            coliviqAccount={coliviqAccount}
            collectionMode={effectiveRevenue.collection_mode}
          />
        </TabsContent>

        <TabsContent value="revenue">
          <RevenueTab
            apartment={apartment}
            rooms={visibleRooms}
            occupants={occupants}
            expectedRent={expectedRent}
            revenue={effectiveRevenue}
            occupied={occupied}
            liveRent={liveRent}
            liveRentLoading={liveRentLoading}
            month={revenueMonth}
            onMonthChange={setRevenueMonth}
          />
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <OnboardingDocumentsCard
            propertyId={apartment.property_id}
            ownerEmail={owner?.email ?? null}
          />
          {apartmentId != null && (
            <ApartmentChecklistCard
              apartmentId={apartmentId}
              bedrooms={apartment.num_rooms ?? 0}
            />
          )}
          {apartmentId != null && propertyId != null && (
            <ApartmentDocumentsTab
              apartmentId={apartmentId}
              propertyId={propertyId}
            />
          )}
        </TabsContent>

        <TabsContent value="notes">
          <NotesPanel
            scope={{ level: "apartment", apartmentId: apartment.id }}
            emptyHint={t(
              "admin.apartments.notesEmptyHint",
              "Anything the next admin should know about this unit — appliance quirks, the lessor's preferences, works in progress."
            )}
          />
        </TabsContent>
      </Tabs>

      {propertyId != null && (
        <>
          <ApartmentEditSheet
            open={editOpen}
            onOpenChange={handleEditOpenChange}
            propertyId={propertyId}
            apartment={apartment}
            focusSection={focusSection}
          />
          <AssignRoomsDialog
            open={assignOpen}
            onOpenChange={setAssignOpen}
            apartmentId={apartment.id}
            propertyId={propertyId}
          />
          <RoomCreateSheet
            open={createRoomOpen}
            onOpenChange={setCreateRoomOpen}
            propertyId={propertyId}
            apartmentId={apartment.id}
          />
        </>
      )}

      {/* Owner profile popup — reuses the admin Owners modal, keyed off the
          apartment's owner. Rendered here so the sidebar and the Owner /
          Contract tabs can all open it. */}
      <OwnerDetailModal
        lessor={ownerModalOpen ? (owner ?? null) : null}
        onClose={() => setOwnerModalOpen(false)}
      />
    </AdminV2Layout>
  );
}

// ─── Owner tab ────────────────────────────────────────────────────────────
function OwnerTab({
  apartment,
  owner,
  ownerInherited,
  ownerAccountUnlinked,
  effectiveRep,
  revenue,
  onOpenOwner,
}: {
  apartment: Apartment;
  owner: Lessor | null;
  /** True when the owner comes from the property, not the apartment itself. */
  ownerInherited?: boolean;
  /** True when an owner account is set but has no lessor record behind it. */
  ownerAccountUnlinked?: boolean;
  /** Representor that applies to this apartment, plus where it came from. */
  effectiveRep: EffectiveRep;
  revenue: EffectiveRevenue;
  /** Opens the owner profile popup. Omitted → owner name is plain text. */
  onOpenOwner?: () => void;
}) {
  const { t } = useLanguage();
  const name = owner?.name ?? t("admin.apartments.unassigned", "Unassigned");
  const pricePerSqm =
    apartment.invested_amount && apartment.size_sqm && apartment.size_sqm > 0
      ? Math.round(apartment.invested_amount / apartment.size_sqm)
      : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-6 lg:col-span-2">
        {!owner && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {ownerAccountUnlinked ? (
                <>
                  {t(
                    "admin.apartments.ownerAccountUnlinkedPre",
                    "This apartment is linked to an owner"
                  )}{" "}
                  <strong>
                    {t("admin.apartments.accountNoun", "account")}
                  </strong>{" "}
                  {t(
                    "admin.apartments.ownerAccountUnlinkedPost",
                    "that has no owner record behind it, so it doesn't appear under Owners and isn't invoiced. Edit the apartment and add an owner, or link a lessor to that account in owner settings."
                  )}
                </>
              ) : (
                <>
                  {t(
                    "admin.apartments.noOwnerLinked",
                    "No owner linked to this apartment yet. Edit the apartment to select or add an owner."
                  )}
                </>
              )}
            </span>
          </div>
        )}
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-teal-100 text-lg font-bold text-teal-700">
            {initialsFromName(name)}
          </div>
          <div>
            {onOpenOwner ? (
              <button
                type="button"
                onClick={onOpenOwner}
                title={t(
                  "admin.apartments.viewOwnerProfile",
                  "View owner profile"
                )}
                className="group flex items-center gap-1 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <h3 className="text-lg font-bold tracking-tight underline-offset-4 group-hover:text-primary group-hover:underline">
                  {name}
                </h3>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
              </button>
            ) : (
              <h3 className="text-lg font-bold tracking-tight">{name}</h3>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge variant="secondary">
                {t("admin.apartments.ownerTab", "Owner")} · {apartment.label}
              </Badge>
              {owner && (
                <InheritanceBadge
                  source={ownerInherited ? "property" : "apartment"}
                  level="apartment"
                />
              )}
            </div>
            {apartment.owner_since && (
              <p className="mt-2 text-xs text-muted-foreground">
                {t("admin.apartments.ownerSince", "Owner since")}{" "}
                {formatDate(apartment.owner_since)}
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5 pt-5 border-t">
          <Field
            label={t("admin.field.email", "Email")}
            value={owner?.email ?? "—"}
          />
          <Field
            label={t("admin.field.phone", "Phone")}
            value={owner?.phone ?? "—"}
          />
          <Field
            label={t("admin.apartments.payout", "Payout")}
            value={apartment.payout_method ?? "—"}
          />
          <Field
            label={t("admin.apartments.since", "Since")}
            value={formatDate(apartment.owner_since) ?? "—"}
          />
        </div>

        {/* Representor — what prints after "vertreten durch" on this
            apartment's contracts. Called out on its own (rather than as one
            more cell in the grid above) because it can differ from the
            owner's default, and the badge is what says which one applies. */}
        <div className="mt-5 pt-5 border-t">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("admin.apartments.representor", "Representor")}
            </p>
            <InheritanceBadge source={effectiveRep.source} level="apartment" />
          </div>
          {effectiveRep.name ? (
            <>
              <p className="mt-1.5 font-medium">{effectiveRep.name}</p>
              {effectiveRep.note?.trim() && (
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {effectiveRep.note.trim()}
                </p>
              )}
            </>
          ) : (
            <p className="mt-1.5 text-sm text-muted-foreground">
              {t(
                "admin.apartments.representorNotSet",
                "Not set — contracts for this apartment will have no “vertreten durch”. Set one here or on the owner."
              )}
            </p>
          )}
        </div>
      </Card>

      <Card className="p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          {t("admin.apartments.investment", "Investment")}
        </p>
        <p className="text-2xl font-bold tracking-tight">
          {formatEUR(apartment.invested_amount) ?? "—"}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {t(
            "admin.apartments.initialInvestmentAmount",
            "Initial investment amount"
          )}
        </p>
        <div className="mt-4 pt-4 border-t space-y-2 text-sm">
          <Field
            label={t("admin.apartments.apartmentSize", "Apartment size")}
            value={
              apartment.size_sqm != null ? `${apartment.size_sqm} m²` : "—"
            }
            inline
          />
          <Field
            label={t("admin.apartments.pricePerSqm", "Price per m²")}
            value={pricePerSqm != null ? (formatEUR(pricePerSqm) ?? "—") : "—"}
            inline
          />
        </div>
      </Card>

      <Card className="p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          {t("admin.apartments.payout", "Payout")}
        </p>
        <p className="text-base font-semibold">
          {apartment.payout_method ?? "—"}
        </p>
        <div className="mt-4 pt-4 border-t space-y-2 text-sm">
          {revenue.revenue_model === "full_rent" ? (
            <Field
              label={t("admin.apartments.masterRent", "Master rent")}
              value={
                apartment.master_rent != null
                  ? `${formatEUR(apartment.master_rent)}/${t("admin.apartments.perMonthSuffix", "mo")}`
                  : "—"
              }
              inline
            />
          ) : revenue.revenue_model === "pct_of_rent" ? (
            <Field
              label={t("admin.apartments.managementFee", "Management fee")}
              value={
                revenue.coliviq_fee_pct != null
                  ? `${revenue.coliviq_fee_pct}% ${t("admin.apartments.ofRent", "of rent")}`
                  : "—"
              }
              inline
            />
          ) : (
            <Field
              label={t("admin.apartments.managementFee", "Management fee")}
              value={
                revenue.coliviq_fee_amount != null
                  ? `${formatEUR(revenue.coliviq_fee_amount)}/room`
                  : "—"
              }
              inline
            />
          )}
          <Field
            label={t("admin.apartments.contractEnd", "Contract end")}
            value={formatDate(apartment.lease_end) ?? "—"}
            inline
          />
        </div>
      </Card>
    </div>
  );
}

// ─── Onboarding documents ───────────────────────────────────────────────
// Signed/produced during the owner onboarding flow, auto-attached here by
// property + owner. Surfaced in the Documents tab (was previously part of
// the now-removed Contract tab — the rest of that tab's content duplicated
// the Owner tab and the apartment edit sheet).
function OnboardingDocumentsCard({
  propertyId,
  ownerEmail,
}: {
  propertyId: number;
  ownerEmail: string | null;
}) {
  const { t } = useLanguage();
  const { data: onboardingDocs = [], isLoading: docsLoading } =
    useOwnerOnboardingDocs(propertyId, ownerEmail);
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-1">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">
          {t("admin.apartments.onboardingDocuments", "Onboarding documents")}
        </h3>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        {t(
          "admin.apartments.onboardingDocumentsDesc",
          "Contracts and service sheets produced during the owner onboarding flow, matched by property"
        )}
        {ownerEmail ? ` ${t("admin.apartments.andOwner", "and owner")}` : ""}.
      </p>
      {docsLoading ? (
        <Skeleton className="h-12 w-full rounded-md" />
      ) : onboardingDocs.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t(
            "admin.apartments.noOnboardingDocuments",
            "No onboarding documents found for this apartment yet."
          )}
        </p>
      ) : (
        <ul className="divide-y">
          {onboardingDocs.map((doc) => (
            <li
              key={doc.key}
              className="flex items-center justify-between gap-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{doc.label}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {doc.ownerName}
                  {doc.submittedAt
                    ? ` · ${formatDate(doc.submittedAt) ?? ""}`
                    : ""}
                </p>
              </div>
              <Button asChild variant="outline" size="sm" className="shrink-0">
                {/* A stored public URL into the contracts bucket, signed on
                    click (COL-436). */}
                <StoredFileLink
                  value={doc.url}
                  bucket="contracts"
                  errorText={t(
                    "admin.docs.openFailed",
                    "Could not open the document."
                  )}
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  {t("common.open", "Open")}
                </StoredFileLink>
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ─── Bank tab ─────────────────────────────────────────────────────────────
function BankTab({
  effectiveBank,
  coliviqAccount,
  collectionMode,
}: {
  effectiveBank: EffectiveBank;
  coliviqAccount: PlatformBankAccount | null;
  collectionMode: string;
}) {
  const { t } = useLanguage();
  const companyCollects = collectionMode === "company_collects";
  // Which account plays which role depends on the collection mode.
  const rentReceiptIsColiviq = companyCollects;

  return (
    <div className="space-y-4">
      <Card className="p-4 text-xs text-muted-foreground">
        {COLLECTION_MODE_LABEL[collectionMode] ?? collectionMode}.{" "}
        {companyCollects
          ? t(
              "admin.apartments.rentToColiviqNetToOwner",
              "Rent is received into the coliviq account; net rent is paid out to the owner account."
            )
          : t(
              "admin.apartments.rentToOwnerFeeToColiviq",
              "Rent is received into the owner account; the management fee is paid to the coliviq account."
            )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Owner account */}
        <Card className="p-6">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h3 className="text-sm font-semibold mb-1">
                {t("admin.apartments.ownerAccount", "Owner account")}
              </h3>
              <p className="text-xs text-muted-foreground">
                {rentReceiptIsColiviq
                  ? t(
                      "admin.apartments.receivesNetRent",
                      "Receives net rent (rent minus fee)."
                    )
                  : t(
                      "admin.apartments.receivesTenantRentDirectly",
                      "Receives tenant rent directly."
                    )}
              </p>
            </div>
            <InheritanceBadge source={effectiveBank.source} level="apartment" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label={t("admin.properties.accountHolder", "Account holder")}
              value={effectiveBank.holder ?? "—"}
            />
            <Field
              label={t("admin.properties.bankName", "Bank name")}
              value={effectiveBank.bank_name ?? "—"}
            />
            <Field
              label={t("admin.properties.iban", "IBAN")}
              value={effectiveBank.iban ?? "—"}
              mono
            />
            <Field
              label={t("admin.properties.bic", "BIC")}
              value={effectiveBank.bic ?? "—"}
              mono
            />
          </div>
        </Card>

        {/* CoLiviq account */}
        <Card className="p-6">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h3 className="text-sm font-semibold mb-1">
                {t("admin.apartments.coliviqAccount", "coliviq account")}
              </h3>
              <p className="text-xs text-muted-foreground">
                {rentReceiptIsColiviq
                  ? t(
                      "admin.apartments.receivesTenantRent",
                      "Receives tenant rent."
                    )
                  : t(
                      "admin.apartments.receivesManagementFee",
                      "Receives the management fee."
                    )}
              </p>
            </div>
            <Badge variant="secondary">
              {coliviqAccount?.is_default
                ? t("admin.apartments.default", "Default")
                : t("admin.apartments.platform", "Platform")}
            </Badge>
          </div>
          {coliviqAccount ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                label={t("admin.properties.accountHolder", "Account holder")}
                value={coliviqAccount.holder}
              />
              <Field
                label={t("admin.properties.bankName", "Bank name")}
                value={coliviqAccount.bank_name ?? "—"}
              />
              <Field
                label={t("admin.properties.iban", "IBAN")}
                value={coliviqAccount.iban}
                mono
              />
              <Field
                label={t("admin.properties.bic", "BIC")}
                value={coliviqAccount.bic ?? "—"}
                mono
              />
            </div>
          ) : (
            <p className="flex items-center gap-1.5 text-xs text-amber-600">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {t(
                "admin.apartments.noColiviqBankConfigured",
                "No coliviq bank account configured yet."
              )}
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}

// ─── Revenue tab ────────────────────────────────────────────────────────
// Exported for ApartmentDetail.revenue.test.tsx: the expected/live figures are
// money, so they are asserted against the rendered DOM, not just the helpers.
export function RevenueTab({
  apartment,
  rooms,
  occupants,
  expectedRent,
  revenue,
  occupied,
  liveRent,
  liveRentLoading,
  month,
  onMonthChange,
}: {
  apartment: Apartment;
  rooms: Room[];
  /** Active occupant per room id, for the per-room breakdown. */
  occupants: Map<number, ActiveOccupant>;
  /** Contracted rent for `month` (see resolveExpectedMonthRent). */
  expectedRent: ApartmentExpectedRent;
  revenue: EffectiveRevenue;
  occupied: number;
  /** Rent actually received for `month` (see useApartmentMonthRent). */
  liveRent: ApartmentMonthRent;
  liveRentLoading: boolean;
  month: string;
  onMonthChange: (month: string) => void;
}) {
  const { t } = useLanguage();
  // Two runs of the same money math: one on contracted rent, one on the rent
  // that actually arrived. Every figure below therefore has an expected and a
  // live reading that cannot drift apart. Each run bills the per-room fee on
  // its own room count — rooms under contract for the forecast (what the
  // invoice generator charges), rooms that actually paid for the live "if the
  // month ended now" reading — so neither can invent a fee on a vacant room or
  // on rent nobody sent.
  const econ = computeApartmentEconomics(
    apartment,
    expectedRent.total,
    revenue,
    expectedRent.rooms
  );
  const liveEcon = computeApartmentEconomics(
    apartment,
    liveRent.paidRent,
    revenue,
    liveRent.paidRooms
  );
  const monthOptions = useMemo(() => recentMonthKeys(12), []);
  // Which hero card the breakdown dialog was opened from. The dialog shows the
  // same per-room table either way; the origin only sets what it leads with.
  const [breakdown, setBreakdown] = useState<"expected" | "live" | null>(null);
  const monthLabel = monthKeyLabel(month);
  const isCurrentMonth = month === currentMonthKey();

  const yieldPct =
    apartment.invested_amount && apartment.invested_amount > 0
      ? ((econ.coliviqRevenue * 12) / apartment.invested_amount) * 100
      : null;

  return (
    <div className="space-y-4">
      {/* Model header */}
      <Card className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold">
              {t("admin.apartments.revenueModelHeading", "Revenue model")} —{" "}
              {apartment.label}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {COLLECTION_MODE_LABEL[revenue.collection_mode] ??
                revenue.collection_mode}
              .
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge>{revenueModelLabel(revenue.revenue_model, t)}</Badge>
            <InheritanceBadge
              source={revenue.source.revenue_model}
              level="apartment"
            />
          </div>
        </div>
        {econ.feeLabel && (
          <p className="mt-3 text-xs text-muted-foreground">
            {t("admin.apartments.managementFee", "Management fee")}:{" "}
            <span className="font-medium">{econ.feeLabel}</span>
          </p>
        )}
      </Card>

      {/* Month header — one selector governs this whole section and the
          sidebar's revenue panel. */}
      <div className="flex items-center justify-between gap-3 pt-2">
        <h3 className="text-base font-semibold">
          {isCurrentMonth
            ? t("admin.apartments.thisMonthsRevenue", "This month's revenue")
            : `${t("admin.apartments.revenueTab", "Revenue")} · ${monthLabel}`}
        </h3>
        {/* Native select on purpose: the month list is a plain choice, and the
            OS menu opens reliably wherever this panel is rendered. */}
        <div className="relative">
          <select
            aria-label={t("admin.apartments.revenueMonthAria", "Revenue month")}
            value={month}
            onChange={(e) => onMonthChange(e.target.value)}
            className="h-9 w-[168px] cursor-pointer appearance-none rounded-md border border-input bg-background pl-3 pr-9 text-sm font-medium shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {monthOptions.map((m) => (
              <option key={m} value={m}>
                {monthKeyLabel(m)}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
        </div>
      </div>

      {/* Contracted vs received — the two headline numbers. Both open the same
          per-room breakdown, so an admin can always see which room or tenant a
          figure is made of instead of trusting a total. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card
          role="button"
          tabIndex={0}
          aria-label={t(
            "admin.apartments.expectedRevenueAria",
            "Expected revenue for {{month}} — show the per-room breakdown"
          ).replace("{{month}}", monthLabel)}
          onClick={() => setBreakdown("expected")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setBreakdown("expected");
            }
          }}
          className="group cursor-pointer p-5 text-left transition-colors hover:border-petrol/40 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("admin.apartments.expectedRevenue", "Expected revenue")}
            </p>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-petrol" />
          </div>
          <p className="text-3xl font-bold tracking-tight tabular-nums">
            {formatEUR(expectedRent.total) ?? "—"}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {/* Says "2 of 3" whenever a room is vacant, so a figure below the
                room total reads as the vacancy it is, not as a missing rent. */}
            {t(
              "admin.apartments.contractedRentAcross",
              "Contracted rent across"
            )}{" "}
            {expectedRent.rooms === rooms.length
              ? `${rooms.length} ${
                  rooms.length === 1
                    ? t("admin.rooms.roomNoun", "room")
                    : t("admin.rooms.roomsNoun", "rooms")
                }`
              : t(
                  "admin.apartments.roomsOfRoomsLet",
                  "{{let}} of {{total}} rooms let"
                )
                  .replace("{{let}}", String(expectedRent.rooms))
                  .replace("{{total}}", String(rooms.length))}{" "}
            {t("common.for", "for")} {monthLabel}
          </p>
        </Card>
        <Card
          role="button"
          tabIndex={0}
          aria-label={t(
            "admin.apartments.liveRevenueAria",
            "Live revenue for {{month}} — show which tenants have paid"
          ).replace("{{month}}", monthLabel)}
          onClick={() => setBreakdown("live")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setBreakdown("live");
            }
          }}
          className="group cursor-pointer border-teal-500 p-5 text-left ring-1 ring-teal-500 transition-colors hover:bg-teal-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-700">
              {t("admin.apartments.liveRevenue", "Live revenue")}
            </p>
            <div className="flex items-center gap-1.5">
              {liveRentLoading ? (
                <Skeleton className="h-5 w-16 rounded-full" />
              ) : (
                <Badge variant="secondary" className="font-normal">
                  {liveRent.billedTenants > 0
                    ? `${liveRent.paidTenants}/${liveRent.billedTenants} ${t("admin.apartments.paidSuffix", "paid")}`
                    : occupied > 0
                      ? t("admin.apartments.notBilledYet", "Not billed yet")
                      : t("admin.apartments.noRentBilled", "No rent billed")}
                </Badge>
              )}
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-teal-700" />
            </div>
          </div>
          {liveRentLoading ? (
            <Skeleton className="h-9 w-40" />
          ) : (
            <p className="text-3xl font-bold tracking-tight tabular-nums text-teal-700">
              {formatEUR(liveRent.paidRent) ?? "—"}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            {t(
              "admin.apartments.paymentsMarkedPaidFor",
              "Payments marked as paid for"
            )}{" "}
            {monthLabel}
          </p>
        </Card>
      </div>

      <MonthRentBreakdownDialog
        open={breakdown !== null}
        origin={breakdown ?? "expected"}
        onClose={() => setBreakdown(null)}
        apartmentLabel={apartment.label}
        monthLabel={monthLabel}
        rooms={rooms}
        occupants={occupants}
        rows={liveRent.rows}
        loading={liveRentLoading}
        expectedByRoom={expectedRent.byRoom}
        expectedTotal={expectedRent.total}
        liveTotal={liveRent.paidRent}
      />

      {/* Money flow breakdown — each line expected against live. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <DualMetricCard
          label={`${t("admin.apartments.rentCollectedBy", "Rent collected by")} ${econ.rentCollector}`}
          expected={formatEUR(econ.rentCollected) ?? "—"}
          live={formatEUR(liveEcon.rentCollected) ?? "—"}
          hint={t("admin.apartments.sumOfRoomRents", "Sum of room rents")}
          loading={liveRentLoading}
        />
        <DualMetricCard
          label={econ.ownerReceivesLabel}
          expected={formatEUR(econ.ownerReceives) ?? "—"}
          live={formatEUR(liveEcon.ownerReceives) ?? "—"}
          hint={
            econ.model === "full_rent"
              ? t("admin.apartments.fixedMasterRent", "Fixed master rent")
              : econ.companyCollects
                ? t(
                    "admin.apartments.ownerReceivesNetRentFromColiviq",
                    "Owner receives net rent from coliviq"
                  )
                : t(
                    "admin.apartments.ownerKeepsRentPaysFeeOut",
                    "Owner keeps rent, pays fee out"
                  )
          }
          loading={liveRentLoading}
        />
        <DualMetricCard
          label={t(
            "admin.apartments.coliviqManagementRevenue",
            "coliviq management revenue"
          )}
          expected={formatEUR(econ.coliviqRevenue) ?? "—"}
          live={formatEUR(liveEcon.coliviqRevenue) ?? "—"}
          hint={econ.coliviqFormulaGeneric}
          expectedAccent={econ.coliviqRevenue > 0}
          liveAccent={liveEcon.coliviqRevenue > 0}
          loading={liveRentLoading}
        />
      </div>

      {/* Yield (only meaningful with an investment base) */}
      {apartment.invested_amount != null && (
        <Card className="p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            {t("admin.apartments.yieldAnalysis", "Yield analysis")}
          </p>
          <div className="space-y-2 text-sm">
            <Field
              label={t(
                "admin.apartments.annualColiviqRevenue",
                "Annual coliviq revenue"
              )}
              value={formatEUR(econ.coliviqRevenue * 12) ?? "—"}
              inline
            />
            <Field
              label={t("admin.apartments.investmentBase", "Investment base")}
              value={formatEUR(apartment.invested_amount) ?? "—"}
              inline
            />
            <Field
              label={t("admin.apartments.grossYield", "Gross yield")}
              value={yieldPct != null ? `${yieldPct.toFixed(1)}%` : "—"}
              inline
            />
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * What the two headline figures are actually made of, room by room: the
 * contracted rent, the rent that arrived, and — the part a total can never
 * show — which rooms are behind. An occupied room with no rent row at all
 * reads "Not billed", because a missing charge looks identical to an unpaid
 * one in every rollup.
 */
function MonthRentBreakdownDialog({
  open,
  origin,
  onClose,
  apartmentLabel,
  monthLabel,
  rooms,
  occupants,
  rows,
  loading,
  expectedByRoom,
  expectedTotal,
  liveTotal,
}: {
  open: boolean;
  /** Which card was clicked — sets the dialog's opening line only. */
  origin: "expected" | "live";
  onClose: () => void;
  apartmentLabel: string;
  monthLabel: string;
  rooms: Room[];
  occupants: Map<number, ActiveOccupant>;
  rows: ApartmentMonthRentRow[];
  loading: boolean;
  /** Contracted rent per room — 0 for a room under no contract this month. */
  expectedByRoom: Map<number, number>;
  expectedTotal: number;
  liveTotal: number;
}) {
  const { t } = useLanguage();
  const byRoom = useMemo(() => {
    const map = new Map<number, ApartmentMonthRentRow[]>();
    for (const r of rows) {
      if (r.roomId == null) continue;
      const list = map.get(r.roomId);
      if (list) list.push(r);
      else map.set(r.roomId, [r]);
    }
    return map;
  }, [rows]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {origin === "live"
              ? t("admin.apartments.rentReceived", "Rent received")
              : t("admin.apartments.rentExpected", "Rent expected")}{" "}
            · {monthLabel}
          </DialogTitle>
          <DialogDescription>
            {apartmentLabel} —{" "}
            {t(
              "admin.apartments.breakdownDialogDesc",
              'contracted rent against what arrived, per room. A vacant room is under no contract, so it is owed nothing. "Live" counts a payment only when Stripe, an approved proof or an admin attestation backs it.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin.rooms.roomColumn", "Room")}</TableHead>
                <TableHead>
                  {t("admin.apartments.tenantColumn", "Tenant")}
                </TableHead>
                <TableHead className="text-right">
                  {t("admin.apartments.expectedColumn", "Expected")}
                </TableHead>
                <TableHead className="text-right">
                  {t("admin.apartments.liveColumn", "Live")}
                </TableHead>
                <TableHead className="text-right">
                  {t("admin.field.status", "Status")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rooms.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-sm text-muted-foreground"
                  >
                    {t(
                      "admin.apartments.noRoomsAssignedYet",
                      "No rooms assigned to this apartment yet."
                    )}
                  </TableCell>
                </TableRow>
              )}
              {rooms.map((room) => {
                const roomRows = byRoom.get(room.id) ?? [];
                const occupant = occupants.get(room.id) ?? null;
                // The row's own tenant is who the month's figures actually
                // belong to — the current occupant is only a fallback for a
                // room with no rows yet (e.g. "not billed" this month), and
                // would otherwise misattribute a past month's rent to
                // whoever lives there today.
                const tenantName =
                  roomRows.find((r) => r.tenantName)?.tenantName ??
                  occupant?.name ??
                  null;
                const paidRows = roomRows.filter((r) => r.paid);
                const live = paidRows.reduce((n, r) => n + r.amount, 0);
                const expected = expectedByRoom.get(room.id) ?? 0;
                return (
                  <TableRow key={room.id}>
                    <TableCell className="font-medium">{room.title}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {tenantName ?? t("admin.apartments.vacant", "Vacant")}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {/* A vacant room is owed nothing, so it shows a dash
                          rather than its list price — otherwise the column
                          would not add up to the Total below it. */}
                      {expected > 0 ? (formatEUR(expected) ?? "—") : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {loading ? (
                        <Skeleton className="ml-auto h-4 w-16" />
                      ) : (
                        (formatEUR(live) ?? "—")
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {loading ? (
                        <Skeleton className="ml-auto h-5 w-16 rounded-full" />
                      ) : (
                        <RoomRentStatusBadge
                          rows={roomRows}
                          hasTenant={!!tenantName}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={2} className="font-semibold">
                  {t("common.total", "Total")}
                </TableCell>
                <TableCell className="text-right font-bold tabular-nums">
                  {formatEUR(expectedTotal) ?? "—"}
                </TableCell>
                <TableCell className="text-right font-bold tabular-nums text-teal-700">
                  {formatEUR(liveTotal) ?? "—"}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A room's rent state for the month. "Unverified" is its own answer on
 * purpose: the column says paid but nothing backs it, so it counts as unpaid
 * everywhere else and rendering it as "Paid" here would contradict the totals.
 */
function RoomRentStatusBadge({
  rows,
  hasTenant,
}: {
  rows: ApartmentMonthRentRow[];
  hasTenant: boolean;
}) {
  const { t } = useLanguage();
  if (rows.length === 0)
    return hasTenant ? (
      <Badge
        variant="outline"
        className="border-amber-300 font-normal text-amber-700"
      >
        {t("admin.apartments.notBilled", "Not billed")}
      </Badge>
    ) : (
      <Badge variant="outline" className="font-normal">
        {t("admin.apartments.vacant", "Vacant")}
      </Badge>
    );

  const paid = rows.filter((r) => r.paid).length;
  if (paid === rows.length)
    return (
      <Badge className="font-normal">
        {t("admin.apartments.paid", "Paid")}
      </Badge>
    );
  if (paid > 0)
    return (
      <Badge variant="secondary" className="font-normal">
        {paid}/{rows.length} {t("admin.apartments.paidSuffix", "paid")}
      </Badge>
    );
  const unverified = rows.some(
    (r) => String(r.status ?? "").toLowerCase() === "paid"
  );
  const overdue = rows.some((r) =>
    ["overdue", "inkasso"].includes(String(r.status ?? "").toLowerCase())
  );
  return (
    <Badge
      variant="outline"
      className={
        unverified
          ? "border-amber-300 font-normal text-amber-700"
          : overdue
            ? "border-destructive/40 font-normal text-destructive"
            : "font-normal"
      }
    >
      {unverified
        ? t("admin.apartments.unverified", "Unverified")
        : overdue
          ? t("admin.apartments.overdueStatus", "Overdue")
          : t("admin.apartments.openStatus", "Open")}
    </Badge>
  );
}

/**
 * One money line read twice: what the contract says, and what actually landed.
 * `expectedAccent`/`liveAccent` mark CoLiviq's own revenue in MetricCard's
 * accent colour — set independently per value, since expected and live can
 * have different signs (a negative live figure shouldn't render as a gain).
 */
function DualMetricCard({
  label,
  expected,
  live,
  hint,
  expectedAccent,
  liveAccent,
  loading,
}: {
  label: string;
  expected: React.ReactNode;
  live: React.ReactNode;
  hint?: string;
  /** Emerald highlight for `expected` — only when that figure is itself positive. */
  expectedAccent?: boolean;
  /** Emerald highlight for `live` — only when that figure is itself positive. */
  liveAccent?: boolean;
  loading?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <Card className="p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
        {label}
      </p>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          {t("admin.apartments.expectedColumn", "Expected")}
        </span>
        <span
          className={`text-base font-bold tabular-nums ${expectedAccent ? "text-emerald-600" : ""}`}
        >
          {expected}
        </span>
      </div>
      <div className="my-2 border-t border-dashed" />
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-medium text-teal-700">
          {t("admin.apartments.liveColumn", "Live")}
        </span>
        {loading ? (
          <Skeleton className="h-5 w-20" />
        ) : (
          <span
            className={`text-base font-bold tabular-nums ${liveAccent ? "text-emerald-600" : ""}`}
          >
            {live}
          </span>
        )}
      </div>
      {hint && <p className="text-[11px] text-muted-foreground mt-2">{hint}</p>}
    </Card>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────
function ApartmentSidebar({
  apartment,
  owner,
  rooms,
  expectedRent,
  occupied,
  effectiveBank,
  coliviqAccount,
  revenue,
  liveRent,
  liveRentLoading,
  revenueMonth,
  ownerInherited,
  onOpenOwner,
}: {
  apartment: Apartment;
  owner: Lessor | null;
  rooms: Room[];
  /** Contracted rent for `revenueMonth` (see resolveExpectedMonthRent). */
  expectedRent: ApartmentExpectedRent;
  occupied: number;
  effectiveBank: EffectiveBank;
  coliviqAccount: PlatformBankAccount | null;
  revenue: EffectiveRevenue;
  /** Rent actually received for `revenueMonth` — the Revenue tab's month. */
  liveRent: ApartmentMonthRent;
  liveRentLoading: boolean;
  revenueMonth: string;
  /** True when the owner comes from the property, not the apartment itself. */
  ownerInherited?: boolean;
  /** Opens the owner profile popup. Omitted → owner name is plain text. */
  onOpenOwner?: () => void;
}) {
  const { t } = useLanguage();
  const name = owner?.name ?? t("admin.apartments.unassigned", "Unassigned");
  const occPct =
    rooms.length > 0 ? Math.round((occupied / rooms.length) * 100) : 0;
  // Derived from active bookings (single source of truth), not the manual
  // apartments.rental_status field.
  const occupancy = deriveApartmentOccupancy(occupied, rooms.length);
  const occupancyLabel =
    occupancy === "occupied"
      ? t("admin.apartments.occupied", "Occupied")
      : occupancy === "partial"
        ? t("admin.apartments.partiallyOccupied", "Partially occupied")
        : t("admin.apartments.vacant", "Vacant");
  // Forecast on contracted rent, billing the fee on the rooms actually under
  // contract — the units the invoice generator charges for.
  const econ = computeApartmentEconomics(
    apartment,
    expectedRent.total,
    revenue,
    expectedRent.rooms
  );
  // Same math on the rent that actually arrived, per-room fee included, so the
  // panel's LIVE column and the Revenue tab's live cards are one calculation,
  // not two.
  const liveEcon = computeApartmentEconomics(
    apartment,
    liveRent.paidRent,
    revenue,
    liveRent.paidRooms
  );
  const flow = resolveMoneyFlow(
    econ.companyCollects,
    effectiveBank,
    coliviqAccount,
    t
  );

  return (
    <div className="space-y-3">
      <Card className="p-4 space-y-2">
        <header className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {t("admin.apartments.ownerTab", "Owner")}
        </header>
        <div className="flex items-center gap-3 pb-3 border-b">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-100 text-sm font-bold text-teal-700">
            {initialsFromName(name)}
          </div>
          <div className="min-w-0">
            {onOpenOwner ? (
              <button
                type="button"
                onClick={onOpenOwner}
                title={t(
                  "admin.apartments.viewOwnerProfile",
                  "View owner profile"
                )}
                className="group flex max-w-full items-center gap-0.5 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <span className="truncate text-sm font-semibold underline-offset-2 group-hover:text-primary group-hover:underline">
                  {name}
                </span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
              </button>
            ) : (
              <p className="text-sm font-semibold truncate">{name}</p>
            )}
            {apartment.owner_since ? (
              <p className="text-xs text-muted-foreground">
                {t("admin.apartments.since", "Since")}{" "}
                {formatDate(apartment.owner_since)}
              </p>
            ) : ownerInherited && owner ? (
              <p className="text-xs text-muted-foreground">
                {t(
                  "admin.apartments.inheritedFromProperty",
                  "Inherited from property"
                )}
              </p>
            ) : null}
          </div>
        </div>
        <div className="space-y-1.5 text-sm pt-1">
          <Field
            label={t("admin.field.email", "Email")}
            value={owner?.email ?? "—"}
            inline
          />
          <Field
            label={t("admin.field.phone", "Phone")}
            value={owner?.phone ?? "—"}
            inline
          />
          <Field
            label={t("admin.apartments.payout", "Payout")}
            value={apartment.payout_method ?? "—"}
            inline
          />
        </div>
      </Card>

      <Card className="p-4 space-y-2">
        <header className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {t("admin.rooms.roomsNoun", "Rooms")}
        </header>
        <div className="space-y-1.5 text-sm">
          <Field
            label={t("admin.apartments.totalLabel", "Total")}
            value={`${rooms.length} ${t("admin.rooms.roomsNoun", "rooms")}`}
            inline
          />
          <Field
            label={t("admin.properties.occupied", "Occupied")}
            value={`${occupied} ${t("admin.rooms.roomsNoun", "rooms")}`}
            inline
          />
          <Field
            label={t("admin.apartments.vacant", "Vacant")}
            value={`${rooms.length - occupied} ${t("admin.rooms.roomsNoun", "rooms")}`}
            inline
          />
          <div className="pt-1">
            <Progress value={occPct} />
          </div>
        </div>
      </Card>

      {/* Revenue — every line contracted (EXP.) against received (LIVE) for
          the month the Revenue tab is showing. */}
      <Card className="p-4 space-y-2 border-teal-500 ring-1 ring-teal-500">
        <header className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t("admin.apartments.revenueTab", "Revenue")}
          </span>
          <Badge variant="secondary" className="font-normal">
            {revenueModelLabel(revenue.revenue_model, t)}
          </Badge>
        </header>
        <div className="space-y-1.5 border-b border-dashed pb-2">
          <div className="grid grid-cols-[1fr_auto_auto] items-baseline gap-x-2 border-b pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <span />
            <span className="text-right">
              {t("admin.apartments.expAbbrev", "Exp.")}
            </span>
            <span className="text-right text-teal-700">
              {t("admin.apartments.liveColumn", "Live")}
            </span>
          </div>
          <SidebarRevenueRow
            label={`${t("admin.apartments.rentLabel", "Rent")} (${econ.rentCollector})`}
            expected={formatEUR(econ.rentCollected) ?? "—"}
            live={formatEUR(liveEcon.rentCollected) ?? "—"}
            loading={liveRentLoading}
          />
          {econ.feeLabel && (
            <SidebarRevenueRow
              label={t("admin.apartments.coliviqFee", "coliviq Fee")}
              expected={econ.feeLabel}
              live={econ.feeLabel}
              loading={false}
            />
          )}
          <SidebarRevenueRow
            label={econ.ownerReceivesShortLabel}
            expected={formatEUR(econ.ownerReceives) ?? "—"}
            live={formatEUR(liveEcon.ownerReceives) ?? "—"}
            loading={liveRentLoading}
          />
          <SidebarRevenueRow
            label={t("admin.apartments.coliviqRevAbbrev", "coliviq rev.")}
            expected={formatEUR(econ.coliviqRevenue) ?? "—"}
            live={formatEUR(liveEcon.coliviqRevenue) ?? "—"}
            loading={liveRentLoading}
          />
        </div>
        <p className="text-[10px] leading-snug text-muted-foreground">
          {t(
            "admin.apartments.liveEqualsPaymentsMarkedPaidFor",
            "Live = payments marked as paid for"
          )}{" "}
          {monthKeyLabel(revenueMonth)}
          {liveRent.billedTenants > 0
            ? ` (${liveRent.paidTenants}/${liveRent.billedTenants} ${t("admin.apartments.tenantPlural", "tenants")}).`
            : occupied > 0
              ? ` — ${t("admin.apartments.notBilledYetLower", "not billed yet")}.`
              : "."}
        </p>
        {apartment.lease_end && (
          <div className="pt-1 text-sm">
            <Field
              label={t("admin.apartments.contract", "Contract")}
              value={`${t("admin.apartments.until", "Until")} ${formatDate(apartment.lease_end)}`}
              inline
            />
          </div>
        )}
      </Card>

      {/* Money flow — both accounts and their roles. The rent-receipt account
          is the one tenants see in their dashboard / Stripe. */}
      <Card className="p-4 space-y-3">
        <header className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {t("admin.apartments.bankAccounts", "Bank accounts")}
        </header>
        <SidebarBankRow
          roleLabel={`${t("admin.apartments.rentReceivedBy", "Rent received by")} ${flow.rentReceipt.party}`}
          account={flow.rentReceipt}
          tenantFacing
        />
        <div className="border-t" />
        <SidebarBankRow
          roleLabel={flow.secondaryLabel}
          account={flow.secondary}
        />
      </Card>

      <Card className="p-4 space-y-2">
        <header className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {t("admin.field.apartment", "Apartment")}
        </header>
        <div className="space-y-1.5 text-sm">
          <Field
            label={t("admin.apartments.labelField", "Label")}
            value={apartment.label}
            inline
          />
          <Field
            label={t("admin.apartments.floor", "Floor")}
            value={apartment.floor ?? "—"}
            inline
          />
          <Field
            label={t("admin.apartments.sizeField", "Size")}
            value={
              apartment.size_sqm != null ? `${apartment.size_sqm} m²` : "—"
            }
            inline
          />
          <Field
            label={t("admin.rooms.roomsNoun", "Rooms")}
            value={apartment.num_rooms != null ? apartment.num_rooms : "—"}
            inline
          />
          <Field
            label={t("admin.field.status", "Status")}
            value={rooms.length > 0 ? <span>{occupancyLabel}</span> : "—"}
            inline
          />
        </div>
      </Card>
    </div>
  );
}

// One account block in the sidebar money-flow card.
function SidebarBankRow({
  roleLabel,
  account,
  tenantFacing,
}: {
  roleLabel: string;
  account: FlowAccount;
  tenantFacing?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {roleLabel}
        </span>
        {tenantFacing && (
          <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 font-normal text-[10px]">
            {t("admin.apartments.tenantsStripe", "Tenants · Stripe")}
          </Badge>
        )}
      </div>
      {account.iban ? (
        <>
          <p className="text-sm font-medium truncate">
            {account.holder ?? account.bank_name ?? account.party}
          </p>
          <p className="text-xs font-mono text-muted-foreground">
            {account.bank_name ? `${account.bank_name} · ` : ""}
            {maskIban(account.iban)}
          </p>
        </>
      ) : (
        <p className="flex items-center gap-1.5 text-xs text-amber-600">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {t("admin.apartments.noAccountSet", "No account set")}
        </p>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────
/** One EXP./LIVE line in the sidebar's revenue panel. */
function SidebarRevenueRow({
  label,
  expected,
  live,
  loading,
}: {
  label: string;
  expected: React.ReactNode;
  live: React.ReactNode;
  loading: boolean;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-baseline gap-x-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-right text-xs font-medium tabular-nums">
        {expected}
      </span>
      {loading ? (
        <Skeleton className="ml-auto h-3.5 w-14" />
      ) : (
        <span className="text-right text-xs font-semibold tabular-nums text-teal-700">
          {live}
        </span>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  inline,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  inline?: boolean;
  mono?: boolean;
}) {
  if (inline) {
    return (
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs text-muted-foreground shrink-0">{label}</span>
        <span
          className={`text-sm font-medium ${mono ? "font-mono text-xs" : ""}`}
        >
          {value}
        </span>
      </div>
    );
  }
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
        {label}
      </p>
      <p className={`text-sm font-medium ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </p>
    </div>
  );
}

function MetricCard({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <Card className="p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {label}
      </p>
      <p
        className={`text-xl font-bold tracking-tight tabular-nums ${accent ? "text-emerald-600" : ""}`}
      >
        {value}
      </p>
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </Card>
  );
}
